/**
 * Timeline timing for in-browser export — a port of the server export semantics
 * (`backend/export_stream_compose.py`, `video_transitions.py`, `server._export_item_trim_window`)
 * so local and server exports place, trim, and blend clips identically.
 */
import { normalizeTransitionOut } from '@/lib/videoTransitions';

export const EXPORT_FPS = 24;

const MIN_TRANSITION_DURATION = 0.1;
const MAX_TRANSITION_DURATION = 2.0;
const DEFAULT_TRANSITION_DURATION = 0.5;

function num(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export function clampTransitionDuration(value, transitionId) {
  if (normalizeTransitionOut(transitionId) === 'cut') return 0;
  const n = Number(value);
  const dur = Number.isFinite(n) ? n : DEFAULT_TRANSITION_DURATION;
  return Math.max(MIN_TRANSITION_DURATION, Math.min(MAX_TRANSITION_DURATION, dur));
}

export function transitionFrameCount(item, fps = EXPORT_FPS) {
  const tid = normalizeTransitionOut(item?.transition_out);
  if (tid === 'cut') return 0;
  const dur = clampTransitionDuration(item?.transition_duration, tid);
  return Math.max(2, Math.round(dur * fps));
}

export function clipPlaybackEndTime(item) {
  const pb = num(item?.playback_seconds) || num(item?.duration_seconds) || 0.01;
  return num(item?.start_time) + pb;
}

/** Output frames a clip occupies (server pads/truncates every clip to this). */
export function clipFrameCount(item, fps = EXPORT_FPS) {
  const { playback } = clipTrimWindow(item);
  return Math.max(1, Math.round(playback * fps));
}

/** `{ trimStart, trimEnd, playback, speed }` — trimEnd already capped to the playable source span. */
export function clipTrimWindow(item) {
  const trimStart = num(item?.trim_start);
  const duration = num(item?.duration_seconds, 5) || 5;
  let trimEnd = item?.trim_end == null ? duration : num(item.trim_end, duration);
  const playback = item?.playback_seconds == null
    ? Math.max(0.01, trimEnd - trimStart)
    : Math.max(0.01, num(item.playback_seconds, 0.01));
  const speed = Math.max(0.25, Math.min(4, num(item?.video_playback_speed, 1) || 1));
  const sourceSpan = Math.max(0.01, playback * speed);
  const cap = num(item?.video_media_duration_seconds);
  if (cap > 0) trimEnd = Math.min(trimEnd, trimStart + cap);
  return {
    trimStart,
    trimEnd: Math.min(trimEnd, trimStart + sourceSpan),
    playback,
    speed,
  };
}

/**
 * Group clips by track. `frameCounts[key]` is how many frames clip `key` renders
 * (0 when its media failed to load).
 */
export function buildTrackPlans(items, frameCounts, fps = EXPORT_FPS) {
  const byTrack = new Map();
  (items || []).forEach((item, key) => {
    const ti = Math.trunc(num(item?.track_index));
    if (!byTrack.has(ti)) byTrack.set(ti, []);
    byTrack.get(ti).push({ key, item });
  });

  return [...byTrack.keys()].sort((a, b) => a - b).map((ti) => {
    const pairs = byTrack.get(ti)
      .slice()
      .sort((a, b) => num(a.item.start_time) - num(b.item.start_time) || a.key - b.key);
    const totalEnd = Math.max(0.01, ...pairs.map(({ item }) => clipPlaybackEndTime(item)));
    const clips = pairs.map(({ key, item }) => ({
      key,
      startIdx: Math.round(num(item.start_time) * fps),
      count: Math.max(0, Math.trunc(num(frameCounts[key]))),
      transitionOut: normalizeTransitionOut(item.transition_out),
      transitionFrames: transitionFrameCount(item, fps),
    }));
    const windows = [];
    for (let i = 1; i < clips.length; i += 1) {
      const prev = clips[i - 1];
      const curr = clips[i];
      if (prev.count <= 0 || curr.count <= 0 || prev.transitionOut === 'cut') continue;
      const n = prev.transitionFrames;
      if (n < 2) continue;
      windows.push({
        start: curr.startIdx - n,
        end: curr.startIdx,
        prevKey: prev.key,
        prevStart: prev.startIdx,
        prevCount: prev.count,
        currKey: curr.key,
        currCount: curr.count,
        tid: prev.transitionOut,
        n,
      });
    }
    return {
      index: ti,
      totalFrames: Math.max(1, Math.ceil(totalEnd * fps)),
      clips,
      windows,
    };
  });
}

export function outputFrameCount(plans) {
  return Math.max(0, ...(plans || []).map((p) => p.totalFrames));
}

/**
 * What to draw at output frame `idx`:
 * `{ op: 'black' }`, `{ op: 'clip', key, j }`, or
 * `{ op: 'blend', prevKey, prevJ, currKey, currJ, tid, progress }`.
 * The highest track covers every frame up to its own end.
 */
export function resolveFrame(plans, idx) {
  let plan = null;
  for (let i = plans.length - 1; i >= 0; i -= 1) {
    if (idx < plans[i].totalFrames) {
      plan = plans[i];
      break;
    }
  }
  if (!plan) return { op: 'black' };

  for (let i = plan.windows.length - 1; i >= 0; i -= 1) {
    const w = plan.windows[i];
    if (idx >= 0 && w.start <= idx && idx < w.end) {
      const k = idx - w.start;
      return {
        op: 'blend',
        prevKey: w.prevKey,
        prevJ: Math.max(0, Math.min(w.prevCount - 1, idx - w.prevStart)),
        currKey: w.currKey,
        currJ: Math.min(k, w.currCount - 1),
        tid: w.tid,
        progress: (k + 1) / w.n,
      };
    }
  }

  for (let i = plan.clips.length - 1; i >= 0; i -= 1) {
    const c = plan.clips[i];
    if (c.count > 0 && c.startIdx <= idx && idx < c.startIdx + c.count) {
      return { op: 'clip', key: c.key, j: idx - c.startIdx };
    }
  }
  return { op: 'black' };
}

/** First output index at which each clip is drawn (used to prefetch media ahead of time). */
export function clipFirstNeededFrame(plans) {
  const first = new Map();
  const put = (key, idx) => first.set(key, Math.min(first.get(key) ?? Infinity, idx));
  for (const plan of plans || []) {
    for (const c of plan.clips) if (c.count > 0) put(c.key, c.startIdx);
    for (const w of plan.windows) {
      put(w.prevKey, w.start);
      put(w.currKey, w.start);
    }
  }
  return first;
}

/** Last output index at which each clip can still be read (sources may be closed after). */
export function clipLastNeededFrame(plans) {
  const last = new Map();
  for (const plan of plans || []) {
    for (const c of plan.clips) {
      last.set(c.key, Math.max(last.get(c.key) ?? -1, c.startIdx + c.count));
    }
    for (const w of plan.windows) {
      last.set(w.prevKey, Math.max(last.get(w.prevKey) ?? -1, w.end));
    }
  }
  return last;
}
